import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '@semantle/shared';
import './LanguageSelector.css';

// Base flag emojis for each language (English is resolved dynamically)
const BASE_LANGUAGE_FLAGS = {
  en: '🇬🇧',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  it: '🇮🇹',
  pt: '🇵🇹',
  el: '🇬🇷',
};

// Resolve which flag to show for a language code.
// For English, show 🇺🇸 when the browser locale is US, otherwise 🇬🇧.
const getLanguageFlag = (code) => {
  if (code === 'en') {
    try {
      if (typeof navigator !== 'undefined') {
        const navLang = (navigator.language || navigator.userLanguage || '').toLowerCase();
        if (navLang.includes('en-us') || navLang.endsWith('-us')) {
          return '🇺🇸';
        }
      }
    } catch (e) {}
    return '🇬🇧';
  }
  return BASE_LANGUAGE_FLAGS[code] || '🌐';
};

function LanguageSelector() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Normalize language code (e.g., 'en-US' -> 'en')
  const rawLanguage = i18n.language || 'en';
  const currentLanguage = rawLanguage.split('-')[0];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const currentFlag = getLanguageFlag(currentLanguage);
  const currentLanguageName = LANGUAGES.find(lang => lang.code === currentLanguage)?.name || 'English';

  return (
    <div className="language-selector" ref={dropdownRef}>
      <button
        className="language-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select language"
      >
        <span className="language-flag">{currentFlag}</span>
        <span className="language-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && (
        <div className="language-dropdown">
          {LANGUAGES.map((lang) => {
            const flag = getLanguageFlag(lang.code);
            const isSelected = lang.code === currentLanguage;
            return (
              <button
                key={lang.code}
                className={`language-option ${isSelected ? 'selected' : ''}`}
                onClick={() => changeLanguage(lang.code)}
              >
                <span className="language-flag">{flag}</span>
                <span className="language-name">{lang.name}</span>
                {isSelected && <span className="language-check">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LanguageSelector;
